/**
 * 登入頁（v2.39.0 改版）
 *
 * 這個 class 只負責「登入邏輯」：state、錯誤訊息、送出。
 * 畫面在 `login/variants.js`（疊層卡片 + 光暈背景），
 * 表單本體在 `login/LoginFields.js`，品牌文案在 `login/brand.js`。
 */
import React, { Component } from 'react';
import { Navigate } from 'react-router-dom';

import axios from 'axios';
import LoginCard from './login/variants';
import { peekPostLogin } from '../../utils/postLoginRedirect';
import './login/login.css';

class Login extends Component {
  constructor(props) {
    super(props);
    this.state = {
      username: '',
      password: '',
      show_login_fail: false,
      version: null,
    };
    // Refs for browser automation compatibility
    this.usernameRef = React.createRef();
    this.passwordRef = React.createRef();
  }

  componentDidMount() {
    // 版本號給卡片顯示。/api 是公開的健康檢查（登入前拿不到 JWT），失敗就不顯示。
    axios.get('/api').then((res) => {
      const v = res && res.data && res.data.version;
      if (v) this.setState({ version: String(v) });
    }).catch(() => {});
    if (typeof this.props.access_token !== "undefined" && !this.props.login) {
      this.props.succedLogin();
    }
  }

  componentDidUpdate(prevProps) {
    // Check if login status changed to failed
    if (prevProps.login !== false && this.props.login === false) {
      this.setState({
        show_login_fail: true
      });
    }
    
    // Check if there's a new error message
    if (prevProps.loginError !== this.props.loginError && this.props.loginError) {
      this.setState({
        show_login_fail: true
      });
    }
    
    // Check fetch status for failures - handle both immutable and plain JS objects
    const fetchStatusChanged = this.props.fetch_status && prevProps.fetch_status;
    if (fetchStatusChanged) {
      // For Immutable.js objects
      if (this.props.fetch_status.get && this.props.fetch_status.get('status') === 'failed' && 
          this.props.fetch_status.get('action') === 'login') {
        this.setState({ show_login_fail: true });
      } 
      // For plain JS objects
      else if (this.props.fetch_status.status === 'failed' && 
               this.props.fetch_status.action === 'login') {
        this.setState({ show_login_fail: true });
      }
    }
  }

  handleChange = (e) => {
    // Map the form field names to state properties
    const fieldMap = {
      'login-username': 'username',
      'login-password': 'password'
    };
    const stateKey = fieldMap[e.target.name] || e.target.name;
    
    this.setState({ [stateKey]: e.target.value });
    // Reset error display when user starts typing
    if (this.state.show_login_fail) {
      this.setState({ show_login_fail: false });
    }
    if (this.props.login === false) this.props.resetLogin();
  }

  // Handle native input events for browser automation compatibility
  handleNativeInput = (e) => {
    const fieldMap = {
      'login-username': 'username',
      'login-password': 'password',
      'username': 'username'
    };
    const stateKey = fieldMap[e.target.name] || e.target.name;
    this.setState({ [stateKey]: e.target.value });
  }

  handleLogin = () => {
    // Get values directly from DOM for browser automation compatibility
    // Fall back to state if refs are not available
    const username = this.usernameRef.current?.value || this.state.username;
    const password = this.passwordRef.current?.value || this.state.password;
    this.props.toLogin(username, password);
  }
  
  render() {
    if (sessionStorage.getItem("access_token")) {
      this.props.getUser();
      this.props.getServerConfig();
      // For initial login, redirect to /admin which will then handle the proper routing
      // 從分享連結（例如 /partner）被導來登入的，登入完回到那裡（只收白名單路徑，見 utils/postLoginRedirect）
      return <Navigate to={peekPostLogin() || '/admin'} />;
    }
    
    // Determine error message to display
    let errorMessage = '';
    if (this.state.show_login_fail) {
      if (this.props.loginError) {
        // Use specific error from Redux state if available
        errorMessage = this.props.loginError;
      } else if (this.props.fetch_status) {
        // Handle both Immutable.js and plain JS objects
        if (this.props.fetch_status.get && this.props.fetch_status.get('error')) {
          // For Immutable.js objects
          errorMessage = this.props.fetch_status.get('error');
        } else if (this.props.fetch_status.error) {
          // For plain JS objects
          errorMessage = this.props.fetch_status.error;
        }
      } else if (this.props.errormsg) {
        // Fall back to errormsg prop
        errorMessage = this.props.errormsg;
      } else {
        // Default error message
        errorMessage = '登入失敗，請確認帳號與密碼。';
      }
    }
    
    // 登入中：按鈕顯示進度並鎖住重複送出
    const fs = this.props.fetch_status;
    const busy = !!fs && (
      (fs.get && fs.get('status') === 'loading' && fs.get('action') === 'login') ||
      (!fs.get && fs.status === 'loading' && fs.action === 'login')
    );

    const formProps = {
      username: this.state.username,
      password: this.state.password,
      onChange: this.handleChange,
      onInput: this.handleNativeInput,
      onSubmit: this.handleLogin,
      usernameRef: this.usernameRef,
      passwordRef: this.passwordRef,
      error: this.state.show_login_fail ? errorMessage : '',
      busy,
    };

    return <LoginCard {...formProps} version={this.state.version} />;
  }
}

export default Login;